import { DateTime } from 'luxon';

import { toApiCalendarDay } from '@/utils/apiDateTime.ts';

import { ChartInterval, intervalToDuration } from '../types.ts';
import { getCappedForwardDuration } from './getCappedForwardDuration.ts';

type AggregatedChartXAxisWindowParams = {
  interval: ChartInterval;
  isLive: boolean;
  isTimeSeriesNormalized: boolean;
  /** Daily or weekly buckets, drawn at local midnight of their API (Oslo) day. */
  hasApiDayBuckets?: boolean;
  from?: DateTime;
  to?: DateTime;
};

/**
 * Resolves the x-axis extremes, in milliseconds, for an aggregated chart.
 *
 * Live charts follow a rolling window ending slightly ahead of now, so the newest point is not
 * glued to the right edge. Historical charts use the configured window.
 *
 * Normalized series are moved to local midnight, and error bars reserve half a day around each
 * point, so an axis ending mid-day keeps the tick for the day after the last measurement. Those
 * charts are snapped to whole days instead, without the forward margin, which would snap into
 * tomorrow when the chart is rendered shortly before midnight. Live windows count back from the
 * start of today's API (Oslo) day, as the request does, so the axis and the data start on the same
 * day in any browser zone. Historical windows snap in the local zone because the configured window
 * may be expressed in UTC.
 *
 * Live charts with API day buckets that are not normalized keep the forward margin, measured from
 * the start of the current API day when that lies ahead of now. West of Oslo the newest bucket sits
 * on the next local midnight for part of the day, and would otherwise fall past the axis end.
 */
export const getAggregatedChartXAxisWindow = ({
  interval,
  isLive,
  isTimeSeriesNormalized,
  hasApiDayBuckets = false,
  from,
  to,
}: AggregatedChartXAxisWindowParams): { min: number | undefined; max: number | undefined } => {
  if (!isLive) {
    if (!isTimeSeriesNormalized) return { min: from?.toMillis(), max: to?.toMillis() };

    const toLocalDay = (value: DateTime) => value.setZone('local').startOf('day').toMillis();
    return { min: from && toLocalDay(from), max: to && toLocalDay(to) };
  }

  const now = DateTime.now();
  const duration = intervalToDuration[interval];

  if (isTimeSeriesNormalized) {
    const today = toApiCalendarDay(now);
    return { min: today.minus(duration).toMillis(), max: today.toMillis() };
  }

  const end = hasApiDayBuckets ? DateTime.max(now, toApiCalendarDay(now)) : now;
  return {
    min: now.minus(duration).toMillis(),
    max: end.plus(getCappedForwardDuration(interval)).toMillis(),
  };
};
